import type { Placement } from '@popperjs/core';
import type { OffsetsFunction } from '@popperjs/core/lib/modifiers/offset';
import type { ReactNode, Ref } from 'react';
import { useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';

export interface DropdownProps {
  className?: string;
  placement?: Placement;
  offset?: OffsetsFunction | [number | null | undefined, number | null | undefined];
  btnClassName: string;
  button: ReactNode | string;
  leaveOpen?: boolean;
  children?: ReactNode;
  poperClass?: string;
  /** Optional data-testid for the dropdown trigger button */
  dataTestId?: string;
  ref?: Ref<DropdownComponentRef>;
}

export interface DropdownComponentRef {
  close: () => void;
}

const Dropdown = (props: DropdownProps) => {
  const { ref: forwardedRef, ...rest } = props;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [visibility, setVisibility] = useState<any>(false);

  // EWM-936: the popper elements are state set through callback refs, not useRef. usePopper
  // needs them during render, and reading ref.current while rendering is not allowed — a ref
  // holds no value on first render, so positioning also relied on an incidental re-render.
  // This is the pattern react-popper documents, and it drops the `any` casts.
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: rest.placement || 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: rest.offset || [0, 0],
        },
      },
    ],
  });

  useEffect(() => {
    // Declared inside the effect and keyed on the two elements: the previous version closed
    // over the first render's handler with a [] dep list, which only worked because refs are
    // mutable. With state that closure would capture nulls forever and click-outside would
    // fire on every click, including clicks inside the dropdown.
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (referenceElement?.contains(target) || popperElement?.contains(target)) {
        return;
      }

      setVisibility(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [referenceElement, popperElement]);

  useImperativeHandle(forwardedRef, () => ({
    close() {
      setVisibility(false);
    },
  }));

  return (
    <>
      <button
        ref={setReferenceElement}
        type='button'
        className={rest.btnClassName}
        data-testid={rest.dataTestId}
        onClick={async () => {
          setVisibility(!visibility);
          await update?.();
        }}
      >
        {rest.button}
      </button>
      {createPortal(
        <div
          ref={setPopperElement}
          role='none' // popper container; close-on-click is a UX convenience, keyboard users access items inside directly
          style={styles.popper}
          {...attributes.popper}
          className={`dropdown ${rest.poperClass || 'z-999'} ${rest.className || ''} `}
          onClick={() => {
            if (!rest.leaveOpen) {
              setVisibility(!visibility);
            }
          }}
        >
          {visibility && rest.children}
        </div>,
        document.body,
      )}
    </>
  );
};

export default Dropdown;
